/**
 * The NOT-RESOLVABLE conflict card — W1, a LinkedIn URL Thrive already holds
 * more than one contact on.
 *
 * Nothing in this product can fix it, and the card has to say so ON THE CARD.
 * Until this it was an untitled paragraph inside a group whose HEADER carried
 * the instruction ("No automatic option — fix these in Thrive first"): easy to
 * miss, and it did not travel with the card, so a recruiter who scrolled past
 * the header met a finding with no stated exit.
 *
 * IT BECAME A DECISION SURFACE ON 2026-09-10 (operator: the section "también
 * tiene que tener los botones"). It had none — the fix genuinely lives in
 * Thrive, so the card named the route and stopped. What that missed is that
 * "go and fix it elsewhere" leaves TWO things the recruiter can still say here:
 * that they went and did it, and that they are not dealing with this one now.
 *
 * The re-check behind the first is NOT the one the different-person card fires.
 * There the row is about to be created, so any surviving holder is a duplicate;
 * here the recruiter was asked to MERGE, and a correct merge leaves exactly
 * one. Hence `mode=thrive_duplicates` — under the default threshold a perfect
 * merge would come back "still colliding" and shake at them for doing it right.
 *
 * ONE CARD PER IMPORT ROW, not per duplicate. The payload carries one
 * `ConflictMember` per Thrive duplicate, all sharing an `import_contact_id`;
 * they are ONE row that matched N contacts, and the grammar puts the row on the
 * left and the N on the right. Rendering the group's members as N separate
 * findings was the old shape and it counted one problem N times.
 */

import type { ConflictGroup } from "@nu-assist/contracts";
import { type HighlightOption, HighlightOptionGroup } from "@nu-assist/ui";
import type { ReactNode } from "react";
import { ConflictCardShell } from "./ConflictCardShell";
import type { RecheckOutcome } from "./DifferentPersonCard";
import {
  CONFLICT_ACTION_CAPTION,
  type DuplicateLinkedInAction,
  RECHECK_CONDITION_THRIVE_DUPLICATES,
  RECHECK_PENDING_TITLE,
  THRIVE_DUPLICATES_ACTION_SENTENCES,
  W1_DETAIL,
  W1_FIX_IN_THRIVE,
  W1_NO_LINK_FALLBACK,
  W1_VERDICT,
} from "./conflictActionsCopy";

export interface W1ConflictCardProps {
  group: ConflictGroup;
  /** Presigned S3 GET URL for the SOURCE row's stored avatar. */
  resolvedAvatarUrl?: string | null;
  /** The chosen decision, or `null` for UNDECIDED — where every card starts and
   *  where two of the three re-check outcomes leave it. */
  action?: DuplicateLinkedInAction | null;
  /** Absent → the options do not render, and the card falls back to the
   *  route-only footer it had before it could decide anything. */
  onActionChange?: (action: DuplicateLinkedInAction) => void;
  /** Both options go disabled while the Thrive read is in flight: one click is
   *  one read. */
  recheckPending?: boolean;
  /** The last verdict for this row — drives the wobble. */
  recheckOutcome?: RecheckOutcome | null;
  /** Changes per answer; makes a REPEATED rejection move again. */
  shakeKey?: number;
  /** The reward slot, filled by the parent only on a cleared verdict and only
   *  behind `?gamification=off`. */
  celebration?: ReactNode;
}

function fullName(first?: string | null, last?: string | null): string {
  return [first, last].filter(Boolean).join(" ").trim();
}

export function W1ConflictCard({
  group,
  resolvedAvatarUrl,
  action = null,
  onActionChange,
  recheckPending = false,
  recheckOutcome = null,
  shakeKey = 0,
  celebration,
}: W1ConflictCardProps) {
  const members = group.contacts ?? [];
  const first = members[0];
  if (!first) return null;

  const sourceName = fullName(first.first_name, first.last_name) || "This row";
  // Members WITHOUT a Thrive id are dropped from the right-hand list rather
  // than rendered as a nameless, unlinkable row. `detect_w1` emits exactly one
  // of those when the lookup knew the URL was shared but could not say by whom,
  // and "In Thrive (1)" over "No name on file" would state a count the card
  // cannot substantiate — the honest reading is that we know of none.
  const thrive = members
    .filter((member) => Boolean(member.contact_id_thrive))
    .map((member) => ({
      thriveId: member.contact_id_thrive ?? null,
      name: fullName(member.thrive_first_name, member.thrive_last_name),
      company: member.thrive_company,
      title: member.thrive_title,
      avatarUrl: member.thrive_avatar_url,
    }));
  const openable = thrive.map((entry) => entry.thriveId).filter((id): id is string => Boolean(id));

  return (
    <ConflictCardShell
      // The FACT that the last check came back "no" — `data-shake`, which is
      // what everything except the eye reads. The MOTION is separate and lives
      // on the chosen option inside the group below. This card passed only the
      // second, so a W1 that had just been rejected looked undecided to every
      // test and every other surface.
      shake={recheckOutcome === "still_colliding"}
      anchorId={first.import_contact_id}
      testId="compare-w1-card"
      source={{
        name: sourceName,
        company: first.company,
        title: first.title,
        linkedinUrl: first.linkedin_url,
        avatarUrl: first.profile_image_s3_key ? resolvedAvatarUrl : undefined,
      }}
      thrive={thrive}
      verdict={W1_VERDICT}
      verdictDetail={W1_DETAIL}
      verdictTone="conflict"
      footer={
        // ONE DIRECTIVE LINE, and no per-duplicate links — the right-hand rows
        // already carry a deep link each, and repeating them here was pure
        // duplication (operator, 2026-09-08).
        //
        // The intended replacement was a bulk "Open all N" that earns its place
        // from three duplicates up. It cannot: a single control opening N tabs
        // needs N `window.open` calls from one gesture, which a popup blocker
        // stops after the first, and the anchors-per-id version that dodges
        // that is N clicks — exactly the N clicks the rows already offer. It
        // saved no work at any N, so it is gone at every N rather than at two.
        //
        // The footer stays non-empty on purpose. A not-resolvable card with no
        // footer reads as broken, which is the failure this whole card kind
        // exists to avoid, and the line does the job the footer is actually
        // for: naming WHERE the fix lives, which no per-row link does.
        onActionChange ? (
          <div className="flex flex-col gap-2">
            {/* The route line SURVIVES above the options, demoted to a caption.
                It is the one thing no per-row link says — WHERE the fix lives —
                and the first option now asks the recruiter to go there, so
                dropping it would remove the instruction the option assumes. */}
            <p data-testid="compare-w1-route" className="text-caption text-text-secondary">
              {openable.length > 0 ? W1_FIX_IN_THRIVE : W1_NO_LINK_FALLBACK}
            </p>
            <p className="text-label text-text-secondary">{CONFLICT_ACTION_CAPTION}</p>
            <HighlightOptionGroup<DuplicateLinkedInAction>
              aria-label="Duplicate contacts in Thrive action"
              data-testid={`w1-actions-${first.import_contact_id}`}
              options={
                [
                  {
                    value: "fixedInThrive",
                    label: THRIVE_DUPLICATES_ACTION_SENTENCES.fixedInThrive,
                    // The threshold, which nothing on this card ever stated:
                    // ONE surviving record, not zero. A recruiter who took a
                    // pile of four down to two had no way to know that was not
                    // enough — the label is their claim, this is ours.
                    description: RECHECK_CONDITION_THRIVE_DUPLICATES,
                    disabled: recheckPending,
                    title: recheckPending ? RECHECK_PENDING_TITLE : undefined,
                    testId: `w1-merged-${first.import_contact_id}`,
                  },
                  {
                    value: "skipForNow",
                    label: THRIVE_DUPLICATES_ACTION_SENTENCES.skipForNow,
                    disabled: recheckPending,
                    title: recheckPending ? RECHECK_PENDING_TITLE : undefined,
                    testId: `w1-skip-${first.import_contact_id}`,
                  },
                ] satisfies HighlightOption<DuplicateLinkedInAction>[]
              }
              value={action}
              onChange={onActionChange}
              shake={
                recheckOutcome === "still_colliding"
                  ? { value: "fixedInThrive", nonce: shakeKey }
                  : null
              }
            />
            {celebration}
          </div>
        ) : (
          // No handler — the card cannot decide anything, so it goes back to
          // being a signpost rather than rendering dead controls.
          <p data-testid="compare-w1-route" className="text-right text-caption text-text-secondary">
            {openable.length > 0 ? W1_FIX_IN_THRIVE : W1_NO_LINK_FALLBACK}
          </p>
        )
      }
    />
  );
}
